import cn from 'classnames';
import React from 'react';
import { useAutoControlled } from '../../hooks';
import { Icon, IconProps } from '../icon/icon';

/**
 * @name            a-menuItem
 * @type            atom
 * @author          Experience One AG
 * @copyright       Robert Bosch GmbH
 *
 * @param           {string}  label           Label to display inside the menu item.
 * @param           {boolean} hasArrowRight   Wheter or not the menu item has an arror right icon.
 * @param           {boolean} hasArrowDown    Wheter or not the menu item has an arror down icon.
 * @param           {boolean} hasDivider      Wheter or not the menu item has a divider after it.
 * @param           {React.ReactElement}  icon        The icon to display if the menu item has one.
 * @param           {boolean} disabled      Wheter or not the menu item is disabled.
 *
 * @description
 * representation of menuItems
 */

export interface MenuItemProps
    extends Omit<React.LiHTMLAttributes<HTMLLIElement>, 'role'> {
    disabled?: boolean;
    label?: string;
    icon?: string | IconProps;
    link?: string;
    children?: React.ReactNode;
}
export interface MenuItemLabelProps
    extends React.HTMLAttributes<HTMLSpanElement> {
    label?: string;
}

export interface MenuItemGroupProps
    extends React.LiHTMLAttributes<HTMLLIElement> {
    expanded?: boolean;
    defaultExpanded?: boolean;
    onGroupClick?: (ev: React.MouseEvent) => void;
    label?: string;
    icon?: string | IconProps;
}

export type MenuItemLinkProps = Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    'role'
>;

export const MenuItemLabel: React.FC<MenuItemLabelProps> = ({
    label,
    children,
    ...rest
}) => {
    return (
        <span className="a-menu-item__label" aria-label={label} {...rest}>
            {children ?? label}
        </span>
    );
};

export const MenuItemGroup: React.FC<MenuItemGroupProps> = ({
    expanded,
    defaultExpanded,
    onGroupClick,
    label,
    icon,
    children,
    ...rest
}) => {
    const [open, setOpen] = useAutoControlled({
        initialValue: false,
        defaultValue: defaultExpanded,
        value: expanded,
    });
    const iconProps =
        typeof icon === 'string' ? { iconName: icon, titleText: icon } : icon;
    const classes = cn('a-menu-item', { '-open': open });

    const handleExpandButtonClick = (ev: React.MouseEvent) => {
        if (typeof onGroupClick === 'function') {
            onGroupClick(ev);
        }
        setOpen(isOpen => !isOpen);
    };

    return (
        <li className={classes} {...rest}>
            <button
                type="button"
                className="a-menu-item__group"
                onClick={handleExpandButtonClick}
                aria-haspopup="true"
                aria-expanded={open}
            >
                {iconProps && <Icon {...iconProps} />}
                {label && <MenuItemLabel label={label} />}
                <Icon
                    iconName={'down'}
                    title={expanded ? 'Collapse' : 'Expand'}
                />
            </button>
            <ul className={`o-context-menu__menuSubitems`} role="menu">
                {children}
            </ul>
        </li>
    );
};

export const MenuItemLink: React.FC<MenuItemLinkProps> = ({
    className,
    children,
    ...rest
}) => {
    const classes = cn('a-menu-item__link', className);
    return (
        <a className={classes} role="menuitem" {...rest}>
            {children}
        </a>
    );
};

export const MenuItem = React.forwardRef<HTMLLIElement, MenuItemProps>(
    ({ disabled, label, icon, link, children, className, ...rest }, ref) => {
        const iconProps = typeof icon === 'string' ? { iconName: icon } : icon;
        const classes = cn(
            'a-menu-item ',
            {
                '-disabled': disabled,
            },
            className,
        );
        const LabelSlot = label ? <MenuItemLabel>{label}</MenuItemLabel> : null;
        const IconSlot = iconProps ? <Icon {...iconProps} /> : null;
        const LinkSlot = LabelSlot ? (
            <MenuItemLink href={link}>
                {IconSlot}
                {LabelSlot}
            </MenuItemLink>
        ) : (
            children
        );
        return (
            <li className={classes} ref={ref} role="none" {...rest}>
                {LinkSlot}
            </li>
        );
    },
);
